/*
 * Search collection match — which collection a search term stands for.
 *
 * The search overlay answers a typed term in one of two ways. When the term NAMES a
 * collection ("Trunks", "Men's Underwear", "Bralette") the panel shows that collection's
 * own first products and Enter opens it, so the overlay and the page it hands off to
 * agree. Anything else ("water bottles", a design name) keeps Shopify's predictive
 * product results and Enter opens shop-all filtered by `?q=`.
 *
 * WHICH collection, in order:
 *
 *   0. The popular-term pill's own link, when the merchant gave it a collection one
 *      (sections/predictive-search.liquid `suggested_query` → `link`). A tag link such as
 *      /collections/accessories/water-bottles narrows the collection to that tag — the
 *      water bottles carry `exclude_search`, so predictive search can never show them.
 *   1. The nav's. The bar's link structure (the Navigation section's blocks, published
 *      by sections/header.liquid as `window.knobbyNavCategories`) is the curated answer
 *      to "where do trunks live" — Shopify's own collection suggestions are not: for
 *      "Trunks" its first hit is "Men's Trunks & Long Leg Trunks". A term matches an
 *      item's title ("Trunks"), a category's ("Men's Underwear" → Mens), or the two
 *      together ("womens pyjamas"). A bare title that means DIFFERENT collections under
 *      different categories ("Pyjamas" is in Mens, Womens and Kids) is not a match here.
 *   2. A Shopify collection whose title or handle IS the term — how "Pyjamas" still
 *      lands on /collections/pyjamas. Exact only: a partial hit is what step 1 exists to
 *      avoid.
 *   3. Nothing.
 *
 * Words are compared loosely — case, apostrophes, ™, a trailing plural `s` and the words
 * "underwear"/"undies" are all ignored — so "mens trunk undies" and "Men's Trunks" are
 * the same term.
 *
 * Pure (no Vue, no DOM, no fetch). Ships as a plain asset attached to
 * `window.KnobbySearchCollectionMatch`; tests import it via `module.exports`.
 */
;(function (global) {
  const IGNORED_WORDS = new Set(['underwear', 'undie', 'undy', 'knobby'])

  const singular = (word) => (word.length > 3 && word.endsWith('s') && !word.endsWith('ss') ? word.slice(0, -1) : word)

  /** "Men's Trunks™" → "men trunk". Empty string for a term with no words left. */
  const normaliseSearchTerm = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/['’]s\b/g, 's')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map(singular)
      .filter((word) => !IGNORED_WORDS.has(word))
      .join(' ')

  /* `/collections/<handle>` or its tag-filtered form `/collections/<handle>/<tag>`. */
  const COLLECTION_LINK = /^\/collections\/([^/?#]+)(?:\/([^/?#]+))?\/?$/

  const isCollectionLink = (link) => COLLECTION_LINK.test(String(link || ''))

  /** "/collections/accessories/water-bottles" → { base: "/collections/accessories", tag: "water-bottles" } */
  const parseCollectionLink = (link) => {
    const m = String(link || '').match(COLLECTION_LINK)
    return m ? { base: `/collections/${m[1]}`, tag: m[2] || '' } : null
  }

  /* Shopify's `handleize`, which is how a tag becomes the last segment of a tag link:
     "Water Bottles" → "water-bottles". */
  const handleize = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')

  const hasTagHandle = (tags, tagHandle) =>
    !tagHandle || (Array.isArray(tags) ? tags : []).some((tag) => handleize(tag) === tagHandle)

  /* Every key a nav entry answers to, mapped to the distinct collections behind it. A
     key with more than one collection is ambiguous and resolves to nothing. */
  const buildNavIndex = (categories) => {
    const index = new Map()
    const add = (key, link, title) => {
      if (!key || !isCollectionLink(link)) return
      if (!index.has(key)) index.set(key, new Map())
      const byUrl = index.get(key)
      if (!byUrl.has(link)) byUrl.set(link, title)
    }
    for (const category of Array.isArray(categories) ? categories : []) {
      const categoryKey = normaliseSearchTerm(category?.title)
      add(categoryKey, category?.link, category?.title)
      for (const group of Array.isArray(category?.groups) ? category.groups : []) {
        for (const column of Array.isArray(group?.columns) ? group.columns : []) {
          for (const item of Array.isArray(column) ? column : []) {
            // "Shop All" is the category's own link under another name; it is reached
            // through the category key above, never as a term of its own.
            if (item?.shopAll) continue
            const itemKey = normaliseSearchTerm(item?.title)
            add(itemKey, item?.link, item?.title)
            if (categoryKey && itemKey) add(`${categoryKey} ${itemKey}`, item?.link, item?.title)
          }
        }
      }
    }
    return index
  }

  const matchNav = (key, categories) => {
    const byUrl = buildNavIndex(categories).get(key)
    if (!byUrl || byUrl.size !== 1) return null
    const [[url, title]] = byUrl
    return { url, title, source: 'nav' }
  }

  const matchPinned = (key, pinned) => {
    for (const entry of Array.isArray(pinned) ? pinned : []) {
      if (normaliseSearchTerm(entry?.term) === key && isCollectionLink(entry?.link)) {
        return { url: entry.link, title: entry.term, source: 'pinned' }
      }
    }
    return null
  }

  /** The pill's link when it is NOT a collection (a product, a page) — opened as is. */
  const pinnedDirectLink = (term, pinned) => {
    const key = normaliseSearchTerm(term)
    if (!key) return ''
    const entry = (Array.isArray(pinned) ? pinned : []).find((e) => normaliseSearchTerm(e?.term) === key)
    return entry?.link && !isCollectionLink(entry.link) ? entry.link : ''
  }

  const matchShopify = (key, collections) => {
    for (const collection of Array.isArray(collections) ? collections : []) {
      // Predictive search appends its tracking params (`?_pos=1&_psq=…`); the page we
      // hand off to wants the bare collection path.
      const url = String(collection?.url || (collection?.handle ? `/collections/${collection.handle}` : '')).split(/[?#]/)[0]
      if (!isCollectionLink(url)) continue
      const names = [collection?.title, String(collection?.handle || '').replace(/-/g, ' ')]
      if (names.some((name) => normaliseSearchTerm(name) === key)) {
        return { url, title: collection.title || '', source: 'shopify' }
      }
    }
    return null
  }

  /**
   * @param {string} term - what the shopper typed
   * @param {{ pinnedTerms?: Array<{term, link}>, navCategories?: Array, shopifyCollections?: Array }} sources
   * @returns {{ url: string, title: string, source: 'pinned' | 'nav' | 'shopify' } | null}
   */
  const resolveSearchCollection = (term, sources) => {
    const key = normaliseSearchTerm(term)
    if (!key) return null
    return (
      matchPinned(key, sources?.pinnedTerms) ||
      matchNav(key, sources?.navCategories) ||
      matchShopify(key, sources?.shopifyCollections)
    )
  }

  /* A product from `/collections/<handle>/products.json` in the shape Shopify's
     predictive search returns, so the overlay's one card normaliser (and its exclusion
     and vault rules) handles both. products.json prices are decimal strings, as the
     predictive ones are. */
  const collectionProductToSuggestShape = (product) => {
    const variants = Array.isArray(product?.variants) ? product.variants : []
    const prices = variants.map((v) => Number(v.price)).filter(Number.isFinite)
    const compares = variants.map((v) => Number(v.compare_at_price)).filter((n) => Number.isFinite(n) && n > 0)
    const image = Array.isArray(product?.images) ? product.images[0] : null
    const priceMin = prices.length ? Math.min(...prices) : 0
    return {
      id: product?.id,
      handle: product?.handle,
      title: product?.title,
      tags: Array.isArray(product?.tags) ? product.tags : [],
      url: product?.handle ? `/products/${product.handle}` : '',
      image: image?.src || '',
      featured_image: image ? { url: image.src, width: image.width, height: image.height } : null,
      price: priceMin.toFixed(2),
      price_min: priceMin.toFixed(2),
      compare_at_price_min: compares.length ? Math.min(...compares).toFixed(2) : '0.00',
      compare_at_price_max: compares.length ? Math.max(...compares).toFixed(2) : '0.00',
      published_at: product?.published_at || null,
      available: variants.some((v) => v.available),
      variants: variants.map((v) => ({
        price: v.price,
        compare_at_price: v.compare_at_price,
        available: v.available
      }))
    }
  }

  const api = {
    normaliseSearchTerm: normaliseSearchTerm,
    resolveSearchCollection: resolveSearchCollection,
    pinnedDirectLink: pinnedDirectLink,
    parseCollectionLink: parseCollectionLink,
    hasTagHandle: hasTagHandle,
    collectionProductToSuggestShape: collectionProductToSuggestShape
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api
  }
  if (global) {
    global.KnobbySearchCollectionMatch = api
  }
})(typeof globalThis !== 'undefined' ? globalThis : typeof self !== 'undefined' ? self : this)
